import React, { MouseEvent, useCallback } from "react";
import { Link } from "react-router-dom";
import useFormatDate from "../../hooks/useFormatDate";

import useTranslations from "../../hooks/useTranslations";
import { H5, Span, Menu, MenuButton, MenuList, MenuItem } from "../wombat-ui";
import { DotsVerticalIcon } from "../icons";
import Favorite from "../utils/Favorite";
import { TemplateFragment } from '../../graphql/generated/definitions';

type TemplateCardProps = {
  template?: TemplateFragment;
  onDelete: (template: TemplateFragment) => void;
  onFavorite: (template: TemplateFragment) => void;
};

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onDelete,
  onFavorite,
}) => {
  const t = useTranslations();
  const formattedUpdatedAt = useFormatDate({
    date: template.updatedAt,
    format: "short",
  });

  const handleDeleteTemplate = useCallback(
    (e) => {
      e.preventDefault();
      onDelete(template);
    },
    [onDelete, template]
  );

  const handleFavorite = useCallback(
    (active: boolean, e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      onFavorite({ ...template, favorite: active });
    },
    [onFavorite]
  );

  return (
    <Link to={`/templates/${template.slug}/edit`} role="button">
      <div
        tabIndex={0}
        role="button"
        className="flex flex-col flex-grow h-full p-1 py-3 rounded-lg shadow-md focus:outline-none ring-4 ring-white bg-light-0 hover:shadow-lg focus:shadow-lg"
      >
        <div className="flex justify-between pl-6 align-center">
          <H5 className="flex items-center justify-between">
            {template.title}
          </H5>
          {template.canDestroy && (
            <Menu>
              <MenuButton
                id={`${template.id}-open-options`}
                icon={DotsVerticalIcon}
                variant="ghost"
                onClick={(e) => e.preventDefault()}
              />
              <MenuList>
                <MenuItem
                  onClick={handleDeleteTemplate}
                  id={`${template.id}-remove`}
                >
                  {t("forms.actions.delete")}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </div>
        <div className="flex justify-between">
          <Span className="px-6 pt-2 text-xs text-light-900">
            {t("templates.lastUpdatedAt", { date: formattedUpdatedAt })}
          </Span>
          {template.canInsert && (
            <Favorite
              id={`${template.id}-favorite`}
              iconClassName="flex items-center px-4"
              iconProps={{ className: "w-4 h-4" }}
              active={template.favorite}
              onClick={handleFavorite}
            />
          )}
        </div>
      </div>
    </Link>
  );
};

export default React.memo(TemplateCard);
